'use strict';

/**
 * Post-login redirect helpers, shared between the backend and the login
 * frontend (Parcel bundles CommonJS from lib/ without trouble).
 *
 * Redirect targets are attacker-influenced (query params, sessionStorage),
 * so everything here is strict-whitelist: relative same-origin paths only.
 */

/**
 * Validates a client-supplied `next` path. Accepts only paths that start
 * with a single `/` — rejects protocol-relative (`//…`), absolute URLs
 * (`…://…`), backslash tricks and control characters. Returns the path
 * unchanged when safe, otherwise null.
 *
 * @param {*} value candidate redirect path
 * @returns {string|null}
 */
const sanitizeNextPath = (value) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed.startsWith('/')) return null;
  if (trimmed.startsWith('//')) return null;
  if (trimmed.includes('\\')) return null;
  if (trimmed.includes('://')) return null;
  // Control characters can smuggle a scheme past naive prefix checks.
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f]/.test(trimmed)) return null;
  return trimmed;
};

/**
 * Maps an enrollment destination ({ bundleId, simId }) to the path the
 * dashboard itself uses to open a simulation. A bundle without a simulation
 * has no direct page, so it falls through to the dashboard (null).
 *
 * @param {{bundleId?: string, simId?: string}|null} destination
 * @returns {string|null} path to land on, or null for the default dashboard
 */
const destinationToPath = (destination) => {
  if (!destination || typeof destination !== 'object') return null;

  const clean = (value) => (typeof value === 'string' && value.trim() ? value.trim() : null);
  const simId = clean(destination.simId);
  const bundleId = clean(destination.bundleId);

  if (simId && bundleId) {
    return `/custom_assessment_adv_vid/${encodeURIComponent(bundleId)}/${encodeURIComponent(simId)}`;
  }
  if (simId) {
    return `/custom_assessment_adv_vid/${encodeURIComponent(simId)}`;
  }
  return null;
};

/**
 * When a `?aid=` token login fails, cookie-auth redirects to `/login` and
 * re-attaches the original query (including `return=` and an appended
 * `next=`). The login page used to take *everything after* the literal
 * `return=` substring — so an empty `return=` grabbed `&next=/…` as the
 * redirect target, which the browser resolved to a path like `/&next=/`
 * and nginx 404'd.
 *
 * Parse `return` properly. Only honour a non-empty absolute http(s) URL
 * (org `failedLoginReturnUrl`). Empty / relative / junk → null (stay on
 * the login page).
 *
 * @param {string} search `location.search` (with or without leading `?`)
 * @returns {string|null}
 */
const resolveAidFailureReturnUrl = (search) => {
  if (typeof search !== 'string' || !search) return null;
  const qs = search.startsWith('?') ? search.slice(1) : search;
  let value;
  try {
    value = new URLSearchParams(qs).get('return');
  } catch (err) {
    return null;
  }
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  // Absolute http(s) only — partner failure URLs. Reject relative junk
  // (`&next=/…`, `/foo`, `//evil`) that would resolve against /login.
  if (!/^https?:\/\//i.test(trimmed)) return null;
  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
    return parsed.toString();
  } catch (err) {
    return null;
  }
};

/**
 * Validates a host return URL for the end of a top-level embedded launch
 * (AUDR-220): `launch_presentation.return_url` from the verified launch, or
 * the registration's default. Absolute `http(s)` only, no credentials in
 * the authority, bounded length; anything else is null (no redirect). The
 * value must come from the sealed embed session — never from the page or
 * the query string — this only guards its shape.
 *
 * @param {*} value candidate URL
 * @returns {string|null} normalised URL or null
 */
const MAX_RETURN_URL = 2048;
const sanitizeReturnUrl = (value) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > MAX_RETURN_URL) return null;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\s]/.test(trimmed)) return null;
  if (!/^https?:\/\//i.test(trimmed)) return null;
  let parsed;
  try {
    parsed = new URL(trimmed);
  } catch (err) {
    return null;
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
  if (parsed.username || parsed.password) return null;
  if (!parsed.hostname) return null;
  return parsed.toString();
};

module.exports = { sanitizeNextPath, destinationToPath, resolveAidFailureReturnUrl, sanitizeReturnUrl };
